import { useQuery } from '@tanstack/react-query'

import { fetchRecursive } from '@/features/areas/api'
import { fetchSitesWithArea } from '@/features/config/api'
import { configQueryKeys } from '@/features/config/query-keys'
import { type SiteWithAreaEntity } from '@/libs/api'
import { type SiteType } from '@/libs/api/types'

export const useSitesWithAreaQuery = (
  query: {
    tenantKey: string
    dcId?: number
    areaId?: number | null
    tourId?: number
    shippingDate?: string
    siteType?: SiteType
    customerId?: number
  },
  options?: { disabled?: boolean }
) => {
  const {
    tenantKey,
    dcId,
    areaId,
    tourId,
    shippingDate,
    siteType,
    customerId
  } = query
  const disabled = options?.disabled ?? false

  return useQuery<SiteWithAreaEntity[]>({
    queryKey: configQueryKeys.sitesWithArea(dcId, areaId, tourId, {
      shippingDate,
      siteType,
      customerId
    }),
    queryFn: ({ signal }) => {
      // customerId指定時は常にfetchSitesWithAreaを使用
      if (query.customerId) {
        return fetchSitesWithArea(
          {
            tenantKey,
            dcId,
            tourId,
            shippingDate,
            siteType,
            customerId,
            areaId
          },
          signal
        )
      }
      // 従来通り：areaId指定時はfetchRecursive、未指定時はfetchSitesWithArea
      return query.areaId
        ? fetchRecursive({ tenantKey, dcId }, query.areaId, signal)
        : fetchSitesWithArea(
            { tenantKey, dcId, tourId, shippingDate, siteType, customerId },
            signal
          )
    },
    enabled: !!tenantKey && !disabled,
    retry: false
    // staleTime は意図的に付けない。このクエリは地点マスタだけでなく、
    // shippingDate/siteType 指定時は当日の未配車案件で使われている地点、tourId 指定時は
    // 最新の運行明細から結果を組み立てる（配車状態に依存する動的クエリ）。長い staleTime を
    // 付けると案件・運行の更新後に古い候補が残るため、既定（マウント毎に最新化）を維持する。
    // ホバーごとの重い再取得は DC 全件を返す useSitesByDcIdSelectQuery 側の staleTime で抑える。
  })
}
